import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { farmAPI } from '../../services/api';
import FlockTimingFields from '../../components/farm/FlockTimingFields';
import { apiDateToInput, dateInputToApi, parseAgeDays } from '../../utils/flockAge';

const FlockList = () => {
  const { farmId } = useParams();
  const [cohorts, setCohorts] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [editValues, setEditValues] = useState({ name: '', type: 'broiler', breed: '', startQty: 0, startDate: '', chickAge: 0, housingType: '', feedingSystem: '', managementSystem: '', subtype: '', targetMarketAgeDays: '' });
  const breedOptions = {
    layer: ['Hy-Line Brown','Hy-Line White','Lohmann Brown','Lohmann White','ISA Brown','Dekalb White','Rhode Island Red','White Leghorn','Black Star','Red Star'],
    broiler: ['Cobb 500','Ross 308','Ross 708','Arbor Acres','Hubbard','Marshall','Indian River','Cornish Cross','Red Ranger','Freedom Ranger'],
    dual: ['Kenbro','Kuroiler','Sasso','Rainbow Rooster','Indigenous (Kienyeji)','Rhode Island Red','Sussex','Plymouth Rock','New Hampshire','Australorp']
  };
  const housingOptions = ['free_range','barn','cage','pasture','deep_litter','other'];
  const feedingOptions = ['manual','automatic','trough','chain_feeder','other'];

  useEffect(() => {
    (async () => {
      try {
        const res = await farmAPI.listCohorts(farmId);
        setCohorts(res.data?.cohorts || []);
      } catch {}
    })();
  }, [farmId]);

  const remove = async (id) => {
    if (!window.confirm('Delete this flock? This action cannot be undone.')) return;
    try {
      await farmAPI.deleteCohort(farmId, id);
      const res = await farmAPI.listCohorts(farmId);
      setCohorts(res.data?.cohorts || []);
      if (editingId === id) setEditingId('');
    } catch (e) {
      console.error('Delete cohort error', e);
      alert('Failed to delete flock');
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditValues({
      name: c.name || '',
      type: c.type || 'broiler',
      breed: c.breed || '',
      startQty: c.startQty || 0,
      startDate: apiDateToInput(c.startDate || c.start_date),
      chickAge: c.initialAgeDays ?? c.chickAge ?? 0,
      housingType: c.housingType || c.housing_type || '',
      feedingSystem: c.feedingSystem || c.feeding_system || '',
      managementSystem: c.managementSystem || c.management_system || '',
      subtype: c.subtype || '',
      targetMarketAgeDays: c.targetMarketAgeDays ?? ''
    });
  };

  const saveEdit = async (id) => {
    await farmAPI.updateCohort(farmId, id, {
      ...editValues,
      startQty: Number(editValues.startQty || 0),
      startDate: dateInputToApi(editValues.startDate),
      initialAgeDays: parseAgeDays(editValues.chickAge),
      targetMarketAgeDays: editValues.targetMarketAgeDays === '' ? undefined : Number(editValues.targetMarketAgeDays)
    });
    const res = await farmAPI.listCohorts(farmId);
    setCohorts(res.data?.cohorts || []);
    setEditingId('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Flocks</h1>
        <p className="text-gray-600">Manage flocks for this farm.</p>
      </div>

      {/* Mobile cards */}
      <div className="space-y-4 md:hidden">
        {cohorts.map((c) => (
          <div key={c.id} className="border rounded-xl p-4 bg-white shadow-sm">
            {editingId === c.id ? (
              <div className="grid grid-cols-1 gap-3">
                <label className="label">Name</label>
                <input className="input" value={editValues.name} onChange={(e)=>setEditValues(p=>({...p,name:e.target.value}))} />
                <label className="label">Type</label>
                <select className="input" value={editValues.type} onChange={(e)=>setEditValues(p=>({...p,type:e.target.value, breed:''}))}>
                  <option value="broiler">Broiler</option>
                  <option value="layer">Layer</option>
                  <option value="dual">Dual-purpose</option>
                </select>
                <label className="label">Breed</label>
                <select className="input" value={editValues.breed} onChange={(e)=>setEditValues(p=>({...p,breed:e.target.value}))}>
                  <option value="">Select breed (optional)</option>
                  {(breedOptions[editValues.type] || []).map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <label className="label">Kienyeji Subtype</label>
                <select className="input" value={editValues.subtype} onChange={(e)=>setEditValues(p=>({...p,subtype:e.target.value}))}>
                  <option value="">Select subtype (optional)</option>
                  <option value="kienyeji_improved">Improved Kienyeji</option>
                  <option value="kienyeji_pure">Pure Kienyeji</option>
                </select>
                <label className="label">Start quantity</label>
                <input type="number" className="input" value={editValues.startQty} onChange={(e)=>setEditValues(p=>({...p,startQty:e.target.value}))} />
                <FlockTimingFields
                  idPrefix={`edit-m-${c.id}-`}
                  type={editValues.type}
                  subtype={editValues.subtype}
                  startDate={editValues.startDate}
                  initialAgeDays={editValues.chickAge}
                  targetMarketAgeDays={editValues.targetMarketAgeDays}
                  onChange={(patch) => setEditValues((p) => ({
                    ...p,
                    ...(patch.startDate !== undefined ? { startDate: patch.startDate } : {}),
                    ...(patch.initialAgeDays !== undefined ? { chickAge: patch.initialAgeDays } : {}),
                    ...(patch.targetMarketAgeDays !== undefined ? { targetMarketAgeDays: patch.targetMarketAgeDays } : {})
                  }))}
                />
                <label className="label">Housing</label>
                <select className="input" value={editValues.housingType} onChange={(e)=>setEditValues(p=>({...p,housingType:e.target.value}))}>
                  <option value="">Select housing (optional)</option>
                  {housingOptions.map(h => <option key={h} value={h}>{h.replace('_',' ')}</option>)}
                </select>
                <label className="label">Management System</label>
                <select className="input" value={editValues.managementSystem} onChange={(e)=>setEditValues(p=>({...p,managementSystem:e.target.value}))}>
                  <option value="">Select management (optional)</option>
                  <option value="intensive">Intensive</option>
                  <option value="semi_intensive">Semi‑intensive</option>
                  <option value="free_range">Free‑range</option>
                </select>
                <label className="label">Feeding</label>
                <select className="input" value={editValues.feedingSystem} onChange={(e)=>setEditValues(p=>({...p,feedingSystem:e.target.value}))}>
                  <option value="">Select feeding (optional)</option>
                  {feedingOptions.map(f => <option key={f} value={f}>{f.replace('_',' ')}</option>)}
                </select>
                <div className="flex gap-2 pt-1">
                  <button className="px-3 py-2 rounded-md border border-primary-600 text-primary-700 text-xs" onClick={()=> saveEdit(c.id)}>Save</button>
                  <button className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> setEditingId('')}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="font-medium text-gray-900">{c.name}</div>
                <div className="text-sm text-gray-700 mt-1">Type: <span className="font-medium">{c.type}</span></div>
                <div className="text-sm text-gray-700">Breed: <span className="font-medium">{c.breed || '—'}</span></div>
                {c.type === 'dual' && (
                  <div className="text-sm text-gray-700">Subtype: <span className="font-medium">{c.subtype || '—'}</span></div>
                )}
                <div className="text-sm text-gray-700">Start qty: <span className="font-medium">{c.startQty}</span></div>
                <div className="text-sm text-gray-700">Arrived: <span className="font-medium">{c.startDate || c.start_date ? apiDateToInput(c.startDate || c.start_date) : '—'}</span></div>
                <div className="text-sm text-gray-700">Age on arrival: <span className="font-medium">{c.initialAgeDays ?? c.chickAge ?? 0} days</span></div>
                <div className="text-sm text-gray-700">Housing: <span className="font-medium">{c.housingType || c.housing_type || '—'}</span></div>
                <div className="text-sm text-gray-700">Management: <span className="font-medium">{c.managementSystem || c.management_system || '—'}</span></div>
                <div className="text-sm text-gray-700">Feeding: <span className="font-medium">{c.feedingSystem || c.feeding_system || '—'}</span></div>
                <div className="flex gap-2 mt-3">
                  <button className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> startEdit(c)}>Edit</button>
                  <button className="px-3 py-2 rounded-md border border-rose-300 text-rose-700 text-xs" onClick={()=> remove(c.id)}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {cohorts.length === 0 && (
          <div className="text-center text-sm text-gray-500">No flocks found.</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hidden md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Subtype</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Breed</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Start Qty</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Arrived</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Age on arrival</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Housing</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Management</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Feeding</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {cohorts.map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? <input className="input" value={editValues.name} onChange={(e)=>setEditValues(p=>({...p,name:e.target.value}))}/> : c.name}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.type} onChange={(e)=>setEditValues(p=>({...p,type:e.target.value, breed: ''}))}>
                      <option value="broiler">Broiler</option>
                      <option value="layer">Layer</option>
                      <option value="dual">Dual-purpose</option>
                    </select>
                  ) : c.type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.subtype} onChange={(e)=>setEditValues(p=>({...p,subtype:e.target.value}))}>
                      <option value="">—</option>
                      <option value="kienyeji_improved">Improved</option>
                      <option value="kienyeji_pure">Pure</option>
                    </select>
                  ) : (c.subtype || '—')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.breed} onChange={(e)=>setEditValues(p=>({...p,breed:e.target.value}))}>
                      <option value="">Select breed (optional)</option>
                      {(breedOptions[editValues.type] || []).map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  ) : (c.breed || '—')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? <input type="number" className="input" value={editValues.startQty} onChange={(e)=>setEditValues(p=>({...p,startQty:e.target.value}))}/> : c.startQty}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <input type="date" className="input" value={editValues.startDate} onChange={(e)=>setEditValues(p=>({...p,startDate:e.target.value}))}/>
                  ) : (c.startDate || c.start_date ? apiDateToInput(c.startDate || c.start_date) : '—')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <input type="number" min="0" className="input" value={editValues.chickAge} onChange={(e)=>setEditValues(p=>({...p,chickAge:e.target.value}))}/>
                  ) : (c.initialAgeDays ?? c.chickAge ?? 0)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.housingType} onChange={(e)=>setEditValues(p=>({...p,housingType:e.target.value}))}>
                      <option value="">Select housing (optional)</option>
                      {housingOptions.map(h => <option key={h} value={h}>{h.replace('_',' ')}</option>)}
                    </select>
                  ) : (c.housingType || c.housing_type || '—')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.managementSystem} onChange={(e)=>setEditValues(p=>({...p,managementSystem:e.target.value}))}>
                      <option value="">Select management (optional)</option>
                      <option value="intensive">Intensive</option>
                      <option value="semi_intensive">Semi‑intensive</option>
                      <option value="free_range">Free‑range</option>
                    </select>
                  ) : (c.managementSystem || c.management_system || '—')}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {editingId === c.id ? (
                    <select className="input" value={editValues.feedingSystem} onChange={(e)=>setEditValues(p=>({...p,feedingSystem:e.target.value}))}>
                      <option value="">Select feeding (optional)</option>
                      {feedingOptions.map(f => <option key={f} value={f}>{f.replace('_',' ')}</option>)}
                    </select>
                  ) : (c.feedingSystem || c.feeding_system || '—')}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  {editingId === c.id ? (
                    <>
                      <button className="px-3 py-1.5 rounded-md border border-primary-600 text-primary-700 text-xs mr-2" onClick={()=> saveEdit(c.id)}>Save</button>
                      <button className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> setEditingId('')}>Cancel</button>
                    </>
                  ) : (
                    <div className="inline-flex gap-2">
                      <button className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 text-xs" onClick={()=> startEdit(c)}>Edit</button>
                      <button className="px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-xs" onClick={()=> remove(c.id)}>Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {cohorts.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">No flocks found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FlockList;


